import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { fetchCourses, fetchMyEnrollments } from '../../lib/lmsApi';
import { fetchCapstoneStatus } from '../../lib/capstoneApi';

// The Diploma is three levels plus two capstone gates, but a student's own
// record was scattered: lesson progress here, exam and essay on /capstone, the
// credential on /certificate. Nothing answered "where am I in the programme?"
// in one place. This page is that view, and it is deliberately read-only --
// every action still belongs to the page that owns it.
//
// No new endpoints were needed: GET /api/capstone/status already returns the
// submission, the best passing attempt and diplomaAwarded in one call.

const CSS = `
  .dash-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1px;
    background: rgba(var(--ap-gold-rgb), 0.22);
    border: 1px solid rgba(var(--ap-gold-rgb), 0.22);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 36px;
  }
  .dash-cell { background: ${theme.bgCard}; padding: 16px 18px; }
  .dash-cell .n {
    font-family: 'Cormorant Garamond', serif;
    font-size: 27px;
    line-height: 1.1;
    color: ${theme.textPrimary};
    display: block;
    font-variant-numeric: tabular-nums;
  }
  .dash-cell .n.met { color: ${theme.gold}; }
  .dash-cell .l {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin-top: 6px;
  }

  .dash-section {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 0 0 14px;
  }

  .progress-track {
    height: 4px;
    background: rgba(var(--ap-gold-rgb), 0.15);
    border-radius: 2px;
    margin-top: 10px;
    overflow: hidden;
  }
  .progress-fill { height: 100%; background: ${theme.gold}; }

  .enrollment-card {
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
    padding: 20px 24px;
    margin-bottom: 12px;
    text-decoration: none;
    display: block;
  }
  .enrollment-card:hover { border-color: rgba(var(--ap-gold-rgb), 0.5); }
  .enrollment-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 14px;
    flex-wrap: wrap;
  }
  .enrollment-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px;
    color: ${theme.textPrimary};
    margin: 0 0 4px;
  }
  .enrollment-meta {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    color: ${theme.textMuted};
  }
  .level-tag {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${theme.gold};
    display: block;
    margin-bottom: 5px;
  }
  .state {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.05em;
    padding: 3px 9px;
    border-radius: 3px;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    color: ${theme.textMuted};
    white-space: nowrap;
  }
  .state.met { color: ${theme.gold}; border-color: rgba(var(--ap-gold-rgb), 0.65); }

  .gate-card {
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-left: 3px solid rgba(var(--ap-gold-rgb), 0.3);
    border-radius: 4px;
    padding: 18px 22px;
    margin-bottom: 12px;
    display: block;
    text-decoration: none;
  }
  .gate-card.met { border-left-color: ${theme.gold}; }
  .gate-note {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12.5px;
    color: ${theme.textMuted};
    margin: 7px 0 0;
    line-height: 1.55;
  }
`;

// The seeded course titles are inconsistent -- "Austrian Economics 101" beside
// "Level 2: ...". The programme is ordered, so the level is derived from the
// slug and the descriptive half of the title shown after it, rather than
// printing three differently-shaped names in a column.
const LEVEL_OF = {
  'austrian-economics-101': 1,
  'austrian-economics-201': 2,
  'austrian-economics-301': 3,
};
const SUBTITLE = {
  'austrian-economics-101': 'Foundations',
  'austrian-economics-201': 'Capital, Money & the Structure of Production',
  'austrian-economics-301': 'Business Cycles, Policy & Applied Austrian Economics',
};

function essayState(submission) {
  switch (submission?.status) {
    case 'passed': return { label: 'Passed', met: true, note: 'Your capstone essay has been accepted.' };
    case 'needs_revision': return { label: 'Returned', met: false, note: 'Returned with notes. Revise and resubmit - there is no limit on attempts.' };
    case 'submitted': return { label: 'With reviewer', met: false, note: 'Submitted and awaiting a decision. You will be emailed when it is graded.' };
    case 'draft': return { label: 'Draft', met: false, note: 'Saved as a draft. It needs 1,500 words and three cited sources to submit.' };
    default: return { label: 'Not started', met: false, note: 'A 2,000–3,000 word essay arguing from the Austrian tradition.' };
  }
}

function MyLearningPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [courses, setCourses] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [capstone, setCapstone] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'My Learning - Austrian Process';
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    // All three run together: the page is a summary, so a partial render is
    // worse than a moment's wait.
    Promise.all([
      fetchCourses(),
      fetchMyEnrollments(getToken),
      fetchCapstoneStatus(getToken),
    ])
      .then(([c, e, s]) => { setCourses(c); setEnrollments(e); setCapstone(s); })
      .catch(() => setError(true));
  }, [getToken, isLoaded, isSignedIn]);

  if (isLoaded && !isSignedIn) {
    return (
      <div className="page-container">
        <PageHeader title="My Learning" description="Sign in to see where you are in the Diploma." />
        <SignInButton mode="modal">
          <button style={{
            fontFamily: "'Josefin Sans', sans-serif", fontSize: 13, letterSpacing: '0.06em',
            padding: '10px 20px', cursor: 'pointer', color: theme.gold,
            background: 'transparent', border: `1px solid ${theme.gold}`, borderRadius: 3,
          }}>Sign in</button>
        </SignInButton>
      </div>
    );
  }

  const loading = !error && (courses === null || enrollments === null || capstone === null);

  // Ordered by level, and every level is listed whether or not it has been
  // started -- a programme view that hid the levels you have not begun would
  // not answer the question the page exists to answer.
  const levels = (courses || [])
    .filter((c) => LEVEL_OF[c.slug])
    .sort((a, b) => LEVEL_OF[a.slug] - LEVEL_OF[b.slug])
    .map((c) => {
      const enrolled = (enrollments || []).find((e) => e.courseId?.slug === c.slug);
      const total = c.lessonCount || 0;
      // The server's resolved count, not completedLessonIds.length. Reseeding a
      // course leaves ids behind that point at no lesson; counting those made
      // this page read higher than the exam gate, which counts only live ones
      // (ADR-0011/0012). A student could see every lesson ticked here and still
      // be refused the exam, with nothing on screen explaining it.
      const done = enrolled?.completedLessonCount ?? 0;
      return { ...c, level: LEVEL_OF[c.slug], total, done, enrolled: Boolean(enrolled) };
    });

  const lessonsDone = levels.reduce((a, l) => a + l.done, 0);
  const lessonsTotal = levels.reduce((a, l) => a + l.total, 0);
  const examPassed = Boolean(capstone?.examPassed);
  const essay = essayState(capstone?.submission);
  const awarded = Boolean(capstone?.diplomaAwarded);

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="My Learning"
        description={user ? `Signed in as ${user.primaryEmailAddress?.emailAddress || user.firstName || 'you'}.` : ''}
      />

      {error && <p style={{ color: theme.error }}>Couldn't load your progress.</p>}
      {loading && <p style={{ color: theme.textMuted }}>Loading...</p>}

      {!loading && !error && (
        <>
          <div className="dash-summary">
            <div className="dash-cell">
              <span className={`n${lessonsTotal && lessonsDone === lessonsTotal ? ' met' : ''}`}>
                {lessonsDone}<span style={{ color: theme.textMuted, fontSize: 18 }}> / {lessonsTotal}</span>
              </span>
              <div className="l">lessons complete</div>
            </div>
            <div className="dash-cell">
              <span className={`n${examPassed ? ' met' : ''}`}>{examPassed ? 'Passed' : 'Open'}</span>
              <div className="l">comprehensive exam</div>
            </div>
            <div className="dash-cell">
              <span className={`n${essay.met ? ' met' : ''}`}>{essay.label}</span>
              <div className="l">capstone essay</div>
            </div>
            <div className="dash-cell">
              <span className={`n${awarded ? ' met' : ''}`}>{awarded ? 'Awarded' : 'Pending'}</span>
              <div className="l">diploma</div>
            </div>
          </div>

          <p className="dash-section">Coursework</p>
          {levels.map((l) => {
            const pct = l.total ? Math.round((l.done / l.total) * 100) : 0;
            const complete = l.total > 0 && l.done === l.total;
            return (
              <Link key={l._id || l.slug} to={`/${l.slug}`} className="enrollment-card">
                <div className="enrollment-head">
                  <div>
                    <span className="level-tag">Level {l.level}</span>
                    <h2 className="enrollment-title">{SUBTITLE[l.slug] || l.title}</h2>
                  </div>
                  <span className={`state${complete ? ' met' : ''}`}>
                    {complete ? 'Complete' : l.enrolled ? `${pct}%` : 'Not started'}
                  </span>
                </div>
                <span className="enrollment-meta">
                  {l.enrolled ? `${l.done} of ${l.total} lessons complete` : `${l.total} lessons`}
                </span>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </Link>
            );
          })}

          <p className="dash-section" style={{ marginTop: 34 }}>Capstone - both gates must be met</p>

          <Link to="/capstone" className={`gate-card${examPassed ? ' met' : ''}`}>
            <div className="enrollment-head">
              <h2 className="enrollment-title">Comprehensive exam</h2>
              <span className={`state${examPassed ? ' met' : ''}`}>{examPassed ? 'Passed' : 'Open'}</span>
            </div>
            <p className="gate-note">
              {examPassed && capstone.bestExamAttempt
                ? `Passed with ${Math.round(capstone.bestExamAttempt.score)}%.`
                : '50 questions across all three levels. 75% to pass, and you can retake it once a day.'}
            </p>
          </Link>

          <Link to="/capstone" className={`gate-card${essay.met ? ' met' : ''}`}>
            <div className="enrollment-head">
              <h2 className="enrollment-title">Capstone essay</h2>
              <span className={`state${essay.met ? ' met' : ''}`}>{essay.label}</span>
            </div>
            <p className="gate-note">{essay.note}</p>
          </Link>

          {awarded && (
            <Link to="/certificate" className="gate-card met" style={{ marginTop: 22 }}>
              <div className="enrollment-head">
                <h2 className="enrollment-title">Your Diploma</h2>
                <span className="state met">Awarded</span>
              </div>
              <p className="gate-note">
                Both gates are met. View, print, or share your certificate and its public verification link.
              </p>
            </Link>
          )}
        </>
      )}
    </div>
  );
}

export default MyLearningPage;
